import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { getThemeColors, THEME_OPTIONS } from '../theme'

export const TERMS_VERSION = '2026-04-19'
export const PRIVACY_VERSION = '2026-04-19'
export const BETA_VERSION = '2026-04-19'

export function hasAcceptedRequiredConsents(user) {
  const metadata = user?.user_metadata ?? {}
  return (
    metadata.terms_version === TERMS_VERSION &&
    metadata.privacy_version === PRIVACY_VERSION &&
    metadata.beta_version === BETA_VERSION &&
    !!metadata.terms_accepted_at &&
    !!metadata.privacy_accepted_at &&
    !!metadata.beta_acknowledged_at
  )
}

export default function BetaConsent({ theme = THEME_OPTIONS.LIGHT }) {
  const C = getThemeColors(theme)
  const navigate = useNavigate()
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [privacyAccepted, setPrivacyAccepted] = useState(false)
  const [betaAccepted, setBetaAccepted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const canContinue = termsAccepted && privacyAccepted && betaAccepted && !loading

  const handleContinue = async () => {
    if (!canContinue) return
    setLoading(true)
    setError('')

    try {
      const acceptedAt = new Date().toISOString()
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          terms_version: TERMS_VERSION,
          terms_accepted_at: acceptedAt,
          privacy_version: PRIVACY_VERSION,
          privacy_accepted_at: acceptedAt,
          beta_version: BETA_VERSION,
          beta_acknowledged_at: acceptedAt,
        },
      })

      if (updateError) throw updateError
      navigate('/dashboard')
    } catch (err) {
      setError(err?.message || 'No pudimos guardar tu aceptación. Intenta de nuevo.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', system-ui, sans-serif", padding: '2rem' }}>
      <div style={{ background: C.white, border: `0.5px solid ${C.border}`, borderRadius: '18px', boxShadow: C.shadow, width: '100%', maxWidth: '680px', padding: '32px' }}>
        <div style={{ fontSize: '15px', fontWeight: '500', marginBottom: '18px' }}>Job<span style={{ color: C.blue }}>Pipeline</span></div>
        <div style={{ fontSize: '28px', fontWeight: '500', color: C.text, lineHeight: '1.15', letterSpacing: '-0.8px', marginBottom: '10px' }}>
          Acceso beta de JobPipeline
        </div>
        <div style={{ fontSize: '14px', color: C.muted, lineHeight: '1.8', marginBottom: '20px' }}>
          Estás entrando a una versión beta controlada. Algunas funciones pueden cambiar, tener errores o ser ajustadas sin previo aviso. Antes de continuar, necesitamos que leas y aceptes nuestros Términos y Condiciones y nuestro Aviso de Privacidad.
        </div>

        <div style={{ background: C.blueLight, color: C.blue, borderRadius: '12px', padding: '14px 16px', fontSize: '13px', lineHeight: '1.7', marginBottom: '20px' }}>
          Esta aceptación queda registrada con la versión vigente de los documentos legales de la beta.
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <button onClick={() => navigate('/terminos?from=beta-access')} style={{ padding: '10px 14px', background: C.white, color: C.blue, border: `0.5px solid ${C.blue}`, borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
            Ver Términos y Condiciones
          </button>
          <button onClick={() => navigate('/privacidad?from=beta-access')} style={{ padding: '10px 14px', background: C.white, color: C.blue, border: `0.5px solid ${C.blue}`, borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
            Ver Aviso de Privacidad
          </button>
        </div>

        <div style={{ display: 'grid', gap: '12px', marginBottom: '20px' }}>
          {[
            [termsAccepted, setTermsAccepted, 'Acepto los Términos y Condiciones de JobPipeline'],
            [privacyAccepted, setPrivacyAccepted, 'He leído y entiendo el Aviso de Privacidad de JobPipeline'],
            [betaAccepted, setBetaAccepted, 'Entiendo que estoy entrando a una beta y que algunas funciones pueden fallar o cambiar'],
          ].map(([checked, setter, label]) => (
            <label key={label} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '14px', color: C.text, lineHeight: '1.7', cursor: 'pointer' }}>
              <input type="checkbox" checked={checked} onChange={(e) => setter(e.target.checked)} style={{ marginTop: '4px' }} />
              <span>{label}</span>
            </label>
          ))}
        </div>

        {error && (
          <div style={{ background: C.redBg, border: `0.5px solid ${C.redBorder}`, color: C.red, borderRadius: '10px', padding: '10px 14px', fontSize: '13px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        <button onClick={handleContinue} disabled={!canContinue} style={{ width: '100%', padding: '12px', background: canContinue ? C.blue : C.border, color: '#fff', border: 'none', borderRadius: '10px', cursor: canContinue ? 'pointer' : 'not-allowed', fontSize: '14px', fontWeight: '500' }}>
          {loading ? 'Guardando...' : 'Continuar a la beta'}
        </button>
      </div>
    </div>
  )
}
